/**
 * Grid.js
 * Manages the grid and its interactions with bubbles
 */

import { Game } from "pixi_core";

import { Common } from "./Common";
import { Bubble } from "./Bubble";
import { GridElement } from "./GridElement";

import {
    BubbleColor,
    GameEvents,
    NB_MAX_COLUMN,
    NB_MAX_ROW,
    GRID_STARTING_OFFSET,
    GRID_BUBBLE_OFFSET,
    NEIGHBORS_OFFSETS,
    BLACK_PANTHER_BASIC,
    BLACK_PANTHER_HARMFUL,
    NB_COLORED_BUBBLES
} from "../Constants";

export class Grid {
    /**
     * @public
     * @param {Object} gridPos
     */
    constructor(gridPos) {
        // Grid is instantiated once and never destroyed
        Common.grid = this;

        /** @private @type {Boolean} */
        this._gridIsOffset = false;

        /** @private @type {GridElement[][]} */
        this._gridElements = [];

        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            this._gridElements.push([]);

            for (let row = 0; row < NB_MAX_ROW; ++row) {
                let offsetY = 0;

                if (column % 2 === 1) {
                    offsetY = GRID_BUBBLE_OFFSET.y / 2;
                }

                let gridElement = new GridElement(column, row);

                gridElement.screenPos.x = gridPos.x + GRID_STARTING_OFFSET.x - GRID_BUBBLE_OFFSET.x * column;
                gridElement.screenPos.y = gridPos.y + offsetY + GRID_STARTING_OFFSET.y + GRID_BUBBLE_OFFSET.y * row;

                this._gridElements[column].push(gridElement);
            }
        }
    }

    /**
     * Switches the offset of the grid on and off
     * @public
     */
    updateGridOffset() {
        this._gridIsOffset = !this._gridIsOffset;
        let offsetY = GRID_BUBBLE_OFFSET.y / 2;

        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            for (let row = 0; row < NB_MAX_ROW; ++row) {
                if (column % 2 === 1) {
                    if (this._gridIsOffset) {
                        this._gridElements[column][row].screenPos.y -= offsetY;
                    } else {
                        this._gridElements[column][row].screenPos.y += offsetY;
                    }
                } else {
                    if (this._gridIsOffset) {
                        this._gridElements[column][row].screenPos.y += offsetY;
                    } else {
                        this._gridElements[column][row].screenPos.y -= offsetY;
                    }
                }
            }
        }
    }

    /**
     * Push the grid left by one vertical column
     * @public
     */
    pushGridLeft() {
        this.updateGridOffset();

        for (let column = NB_MAX_COLUMN - 1; column > 0; --column) {
            // Clear column
            for (let row = 0; row < NB_MAX_ROW; ++row) {
                this._gridElements[column][row].bubble = null;
            }

            // Move bubbles left one column
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                const element = this._gridElements[column][row];
                element.bubble = this._gridElements[column - 1][row].bubble;
                if (element.bubble !== null) {
                    const position = element.screenPos;
                    element.bubble.setPos(position.x, position.y);
                    element.bubble.setGridPos(row, column);
                }
            }
        }

        // Clear the first vertical column of bubbles (where new bubbles will be spawned)
        for (let row = 0; row < NB_MAX_ROW; ++row) {
            this._gridElements[0][row].bubble = null;
        }

        this.considerDamageZone();
    }

    /**
     * Destroy bubbles in the damage zone (last vertical column)
     * @public
     */
    considerDamageZone() {
        let bubblesInDamageZone = [];
        const maxRow = this.getNbRows(NB_MAX_COLUMN - 1);
        for (let row = 0; row < maxRow; ++row) {
            const element = this._gridElements[NB_MAX_COLUMN - 1][row];
            if (element.bubble !== null) {
                element.bubble.popLastRow();
                bubblesInDamageZone.push(element.bubble);
                element.bubble = null;
            }
        }

        if (bubblesInDamageZone.length !== 0) {
            Common.collisions.removeBubbles(bubblesInDamageZone);
            this.removeBubbles(bubblesInDamageZone);

            // TODO: Make sure bubbles in damage zone are properly destroyed

            // Damage event
            const damageDealt = bubblesInDamageZone.length * Common.settings.heroBaseDamage;
            Game.fire(GameEvents.eHEROES_HIT, damageDealt, null);
        }

        Game.fire(GameEvents.eGAME_SET_WARNING_LINE_OPACITY, this._getFirstColumn());
    }

    /**
     * Adds a bubble to the grid
     * @public
     * @param {Bubble} bubble
     */
    addBubble(bubble, column, row) {
        bubble.column = column;
        bubble.row = row;
        this._gridElements[column][row].bubble = bubble;

        Game.fire(GameEvents.eGAME_SET_WARNING_LINE_OPACITY, this._getFirstColumn());
    }

    /**
     * Removes a bubble from the grid
     * @public
     * @param {Bubble} bubble
     */
    removeBubble(bubble) {
        if (bubble.column >= 0 || bubble.row >= 0) {
            this._gridElements[bubble.column][bubble.row].bubble = null;
        }

        Game.fire(GameEvents.eGAME_SET_WARNING_LINE_OPACITY, this._getFirstColumn());
    }

    /**
     * Removes an array of bubbles from the grid
     * @public
     * @param {Bubble[]} bubbles
     */
    removeBubbles(bubbles) {
        for (const bubble of bubbles) {
            if (bubble.column >= 0 || bubble.row >= 0) {
                this._gridElements[bubble.column][bubble.row].bubble = null;
            }
        }

        Game.fire(GameEvents.eGAME_SET_WARNING_LINE_OPACITY, this._getFirstColumn());
    }

    /**
     * Clips a bubble to a grid position
     * @public
     * @param {Bubble} bubble
     * @returns {Boolean} - Returns whether the bubble was successfully clipped to the grid
     */
    clipBubblePositionToGrid(bubble) {
        let column = Math.round((GRID_STARTING_OFFSET.x - bubble.x) / GRID_BUBBLE_OFFSET.x);
        let offsetY = this._getColumnOffset(column);
        let row = Math.round((bubble.y - GRID_STARTING_OFFSET.y - offsetY) / GRID_BUBBLE_OFFSET.y);

        // Check invalid column values
        if (column < 0 || column >= NB_MAX_COLUMN) {
            console.error("[Grid.clipBubblePositionToGrid] bubble x position is out of the grid");
            return false;
        }

        // Force a valid row value, in case a bubble is fired on the wall edges
        if (row < 0) row = 0;
        const maxRow = this.getNbRows(column);
        if (row >= maxRow) row = maxRow - 1;

        let tile = this._gridElements[column][row];

        // If the grid position is occupied, try the nearest adjacent empty tile
        if (tile.bubble) {
            tile = this._findNearestNeighborEmptyTile(column, row, bubble.x, bubble.y);
            if (!tile) {
                console.error("[Grid.clipBubblePositionToGrid] Grid position is occupied and there is no neighbor empty tile");
                return false;
            }
        }

        const gridPos = tile.screenPos;
        bubble.setPos(gridPos.x, gridPos.y);

        tile.bubble = bubble;

        bubble.column = column;
        bubble.row = row;

        // Success
        return true;
    }

    /**
     * TODO: Consolidate clipPositionToGrid() and clipBubblePositionToGrid() to avoid duplicate code
     * Clips a position to a grid position
     * @public
     * @param {{x: Number, y: Number}} position
     */
    clipPositionToGrid(position) {
        let column = Math.round((GRID_STARTING_OFFSET.x - position.x) / GRID_BUBBLE_OFFSET.x);
        let offsetY = this._getColumnOffset(column);
        let row = Math.round((position.y - GRID_STARTING_OFFSET.y - offsetY) / GRID_BUBBLE_OFFSET.y);

        // Check invalid column values
        if (column < 0 || column >= NB_MAX_COLUMN) {
            console.error("[Grid.clipPositionToGrid] position.x is out of the grid");
            return;
        }

        // Force a valid row value, in case a bubble is fired on the wall edges
        if (row < 0) row = 0;
        const maxRow = this.getNbRows(column);
        if (row >= maxRow) row = maxRow - 1;

        let tile = this._gridElements[column][row];

        // If the grid position is occupied, try the nearest adjacent empty tile
        if (tile.bubble) {
            tile = this._findNearestNeighborEmptyTile(column, row, position.x, position.y);
            if (!tile) {
                console.error("[Grid.clipPositionToGrid] Grid position is occupied and there is no neighbor empty tile");
                return false;
            }
        }

        const gridPos = tile.screenPos;
        position.x = gridPos.x;
        position.y = gridPos.y;
    }

    /**
     * @private
     * @param {Number} column
     * @returns {Number}
     */
    _getColumnOffset(column) {
        if (this._gridIsOffset) {
            if (column % 2 === 0) {
                return GRID_BUBBLE_OFFSET.y / 2;
            }
        } else {
            if (column % 2 === 1) {
                return GRID_BUBBLE_OFFSET.y / 2;
            }
        }
        return 0;
    }

    /**
     * Return the offset status of the grid
     * @public
     * @returns {Boolean}
     */
    get isOffset() {
        return this._gridIsOffset;
    }

    /**
     * @public
     * @param {Number} column
     * @param {Number} row
     * @param {Number} x
     * @param {Number} y
     * @returns {GridElement}
     */
    _findNearestNeighborEmptyTile(column, row, x, y) {
        let nearestEmptyTile = null;
        let minDistance = Infinity;

        const neighborsOffsets = this._gridIsOffset ? NEIGHBORS_OFFSETS[1 - (column % 2)] : NEIGHBORS_OFFSETS[column % 2];

        for (const neighborOffset of neighborsOffsets) {
            let neighborColumn = column + neighborOffset.column;
            let neighborRow = row + neighborOffset.row;

            const maxRow = this.getNbRows(neighborColumn);
            if (neighborColumn >= 0 && neighborColumn < NB_MAX_COLUMN && neighborRow >= 0 && neighborRow < maxRow) {
                let tile = this._gridElements[neighborColumn][neighborRow];
                if (tile.bubble === null) {
                    let deltaX = tile.x - x;
                    let deltaY = tile.y - y;
                    let distanceSquared = deltaX * deltaX + deltaY * deltaY;
                    if (distanceSquared < minDistance) {
                        minDistance = distanceSquared;
                        nearestEmptyTile = tile;
                    }
                }
            }
        }

        return nearestEmptyTile;
    }

    /**
     * Converts a grid position (row, column) to an x,y position in the screen.
     * @public
     * @param {Number} column
     * @param {Number} row
     */
    convertGridPosToScreenPos(column, row) {
        const screenPos = { x: this._gridElements[column][row].screenPos.x, y: this._gridElements[column][row].screenPos.y };
        return screenPos;
    }

    /**
     * Returns a bubble at specific grid coordinates
     * @public
     * @param {Number} column
     * @param {Number} row
     * @returns {Bubble}
     */
    getBubble(column, row) {
        if (this._gridElements[column] && this._gridElements[column][row]) {
            return this._gridElements[column][row].bubble;
        } else return null;
    }

    /**
     * Get the adjacent bubbles to the specified grid position
     * @public
     * @param {Number} column
     * @param {Number} row
     * @returns {Bubble[]}
     */
    getNeighborBubbles(column, row) {
        const neighbors = [];

        const neighborsOffsets = this._gridIsOffset ? NEIGHBORS_OFFSETS[1 - (column % 2)] : NEIGHBORS_OFFSETS[column % 2];

        for (const neighborOffset of neighborsOffsets) {
            const neighborColumn = column + neighborOffset.column;
            const neighborRow = row + neighborOffset.row;

            const maxRow = this.getNbRows(neighborColumn);
            if (neighborColumn >= 0 && neighborColumn < NB_MAX_COLUMN && neighborRow >= 0 && neighborRow < maxRow) {
                if (this._gridElements[neighborColumn][neighborRow].bubble) {
                    neighbors.push(this._gridElements[neighborColumn][neighborRow].bubble);
                }
            }
        }

        return neighbors;
    }

    /**
     * Get the adjacent tiles to the specified grid position
     * @public
     * @param {Number} column
     * @param {Number} row
     * @returns {GridElement[]}
     */
    getNeighborTiles(column, row) {
        const neighbors = [];

        const neighborsOffsets = this._gridIsOffset ? NEIGHBORS_OFFSETS[1 - (column % 2)] : NEIGHBORS_OFFSETS[column % 2];

        for (const neighborOffset of neighborsOffsets) {
            let neighborColumn = column + neighborOffset.column;
            let neighborRow = row + neighborOffset.row;

            const maxRow = this.getNbRows(neighborColumn);
            if (neighborColumn >= 0 && neighborColumn < NB_MAX_COLUMN && neighborRow >= 0 && neighborRow < maxRow) {
                neighbors.push(this._gridElements[neighborColumn][neighborRow]);
            }
        }

        return neighbors;
    }

    /**
     * @public
     * @returns {Bubble[]}
     */
    getFirstColumnBubbles() {
        const firstColumnBubbles = [];

        const column = 0;
        const maxRow = this.getNbRows(0);
        for (let row = 0; row < maxRow; ++row) {
            let bubble = this._gridElements[column][row].bubble;
            if (bubble) {
                firstColumnBubbles.push(bubble);
            }
        }

        return firstColumnBubbles;
    }

    /**
     * Returns all empty tiles connected to bubbles or on the first column
     * @public
     * @param {Number} startColumn
     * @returns {GridElement[]}
     */
    getConnectedEmptyTiles(startColumn = 0, endColumn = NB_MAX_COLUMN) {
        /** @type {GridElement[]} */
        const connectedEmptyTiles = [];

        // First column
        const maxFirstRow = this.getNbRows(0);
        for (let row = 0; row < maxFirstRow; ++row) {
            let tile = this._gridElements[0][row];
            if (!tile.bubble) {
                if (startColumn === 0) {
                    connectedEmptyTiles.push(tile);
                }
                tile.processed = true;
            }
        }

        // Connected empty tiles
        for (let column = startColumn; column < endColumn; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._gridElements[column][row].bubble;
                if (bubble) {
                    const neighbors = this.getNeighborTiles(column, row);
                    for (const tile of neighbors) {
                        if (!tile.bubble && !tile.processed) {
                            connectedEmptyTiles.push(tile);
                            tile.processed = true;
                        }
                    }
                }
            }
        }

        // Reset processed tiles
        for (const tile of connectedEmptyTiles) {
            tile.processed = false;
        }

        return connectedEmptyTiles;
    }

    /**
     * Returns all basic colored bubbles
     * @public
     * @param {Boolean} noSpecial
     * @returns {Bubble[]}
     */
    getColoredBubbles(noSpecial) {
        /** @type {Bubble[]} */
        const bubbles = [];

        // Find bubbles
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._gridElements[column][row].bubble;

                if (
                    bubble &&
                    bubble.color < BubbleColor.eSPECIAL &&
                    (!noSpecial || bubble.specialType >= BubbleColor.eRANDOM || bubble.specialType < BubbleColor.eSPECIAL)
                ) {
                    bubbles.push(bubble);
                }
            }
        }

        return bubbles;
    }

    /**
     * Returns all bubbles with a special type different than the one specified
     * @public
     * @param {BubbleColor} specialType
     * @returns {Bubble[]}
     */
    getColoredBubblesOfDifferentSpecialType(specialType) {
        /** @type {Bubble[]} */
        const bubbles = [];

        // Find bubbles
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._gridElements[column][row].bubble;

                if (specialType === BubbleColor.eSHIELDED) {
                    if (bubble && bubble.color < BubbleColor.eSPECIAL) {
                        bubbles.push(bubble);
                    }
                } else {
                    if (bubble && bubble.color < BubbleColor.eSPECIAL && bubble.specialType !== specialType) {
                        bubbles.push(bubble);
                    }
                }
            }
        }

        return bubbles;
    }

    /**
     * Is there any bubble with the specified color?
     * @public
     * @param {BubbleColor} color
     * @returns {Boolean}
     */
    anyBubbleOfColor(color) {
        // Find bubbles
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._gridElements[column][row].bubble;

                if (bubble && bubble.color === color) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Returns all bubbles of the specified color
     * @public
     * @param {BubbleColor} color
     * @returns {Bubble[]}
     */
    getBubblesOfColor(color) {
        let bubbles = [];

        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._gridElements[column][row].bubble;
                if (bubble && bubble.color === color) {
                    bubbles.push(bubble);
                }
            }
        }

        return bubbles;
    }

    /**
     * Is there any bubble with the specified special type?
     * @public
     * @param {BubbleColor} specialType
     * @returns {Boolean}
     */
    anyBubbleOfSpecialType(specialType) {
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._gridElements[column][row].bubble;

                if (bubble && bubble.specialType === specialType) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Return all the colours present in the grid
     * @public
     * @returns {BubbleColor[]}
     */
    getAllGridColors() {
        let bubbleColors = [];
        let coloredBubbles = this.getColoredBubbles(false);

        for (let i = 0; i < coloredBubbles.length; ++i) {
            let colorFound = false;

            for (let j = 0; j < bubbleColors.length; ++j) {
                if (bubbleColors[j] === coloredBubbles[i].color) {
                    colorFound = true;
                }
            }

            if (!colorFound) {
                bubbleColors.push(coloredBubbles[i].color);
            }

            if (bubbleColors.length >= NB_COLORED_BUBBLES) {
                break;
            }
        }

        return bubbleColors;
    }

    /**
     * Returns all bubbles for Black Panther's special ability
     * @public
     * @returns {Bubble[][]}
     */
    getBubblesForBlackPanther() {
        /** @type {Bubble[][]} */
        const bubbles = [[], [], []];

        // Find bubbles
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._gridElements[column][row].bubble;
                if (bubble === null) continue;

                if (
                    bubble.color === BubbleColor.eCOLORLESS ||
                    bubble.color === BubbleColor.eSTONE ||
                    bubble.specialType === BubbleColor.eSHIELDED ||
                    bubble.specialType === BubbleColor.eCHAMELEON ||
                    bubble.color === BubbleColor.eBLOOMING ||
                    bubble.color === BubbleColor.eSTONE_BLOOMING
                ) {
                    bubbles[BLACK_PANTHER_HARMFUL].push(bubble);
                } else {
                    bubbles[BLACK_PANTHER_BASIC].push(bubble);
                }
            }
        }

        return bubbles;
    }

    /**
     * Returns all the grid's bubbles
     * @public
     * @returns {Bubble[]}
     */
    getAllGridBubbles() {
        let bubbles = [];

        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                let bubble = this._gridElements[column][row].bubble;
                if (bubble) {
                    bubbles.push(bubble);
                }
            }
        }

        return bubbles;
    }

    /**
     * Is the grid completely empty of bubbles?
     * @public
     * @returns {Boolean}
     */
    isGridEmpty() {
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            const maxRow = this.getNbRows(column);
            for (let row = 0; row < maxRow; ++row) {
                if (this._gridElements[column][row].bubble !== null) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * Get the number of rows in the specified column
     * @public
     * @param {Number} column
     * @returns {Number}
     */
    getNbRows(column) {
        return column % 2 === Number(this._gridIsOffset) ? NB_MAX_ROW : NB_MAX_ROW - 1;
    }

    /**
     * Get the first column with at least one bubble in the current grid
     * @private
     * @returns {Number}
     */
    _getFirstColumn() {
        let firstColumn = -1;

        for (let i = 0; i < NB_MAX_COLUMN; ++i) {
            for (let j = 0; j < NB_MAX_ROW; ++j) {
                if (this._gridElements[i][j].bubble !== null) {
                    if (firstColumn < i) {
                        firstColumn = i;
                    }
                }
            }
        }
        return firstColumn;
    }

    /**
     * Reset offset
     * @public
     */
    resetGridOffset() {
        if (this._gridIsOffset) {
            this.updateGridOffset();
        }
    }

    /**
     * Clears the grid after closing a level
     * @public
     */
    clear() {
        // Clears all bubbles in the grid
        for (let column = 0; column < NB_MAX_COLUMN; ++column) {
            for (let row = 0; row < NB_MAX_ROW; ++row) {
                let tile = this._gridElements[column][row];
                if (tile.bubble) {
                    tile.bubble.destroy();
                    tile.bubble = null;
                }
            }
        }

        this.resetGridOffset();
    }
}
